import { useState, useEffect, useCallback } from 'react';
import AuthGuard from '@/components/feature/AuthGuard';
import OrganizerLayout from '../components/OrganizerLayout';
import { supabase } from '@/lib/supabase';

interface TeamDetail {
  id: string;
  registration_id: string;
  team_name: string;
  college_name: string;
  department: string;
  city: string;
  team_email: string;
  team_phone: string;
  status: string;
  has_faculty_mentor: boolean;
  participants: { id: string; full_name: string; email: string; contact_number: string; year_of_study: string; department: string; is_leader: boolean }[];
  faculty: { id: string; name: string; designation: string; department: string; contact_number: string; email: string } | null;
  payment: { id: string; status: string; amount: number } | null;
}

export default function OrganizerTeams() {
  const [teams, setTeams] = useState<TeamDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterPayment, setFilterPayment] = useState('All');
  const [selectedTeam, setSelectedTeam] = useState<TeamDetail | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const fetchTeams = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const { data: teamsData, error: teamsErr } = await supabase.from('teams').select('id,registration_id,team_name,college_name,department,city,team_email,team_phone,status,has_faculty_mentor').order('created_at', { ascending: false });
      if (teamsErr) throw teamsErr;
      if (!teamsData || teamsData.length === 0) { setTeams([]); setLoading(false); return; }

      const teamIds = teamsData.map((t) => t.id);
      const [{ data: parts }, { data: ments }, { data: pays }] = await Promise.all([
        supabase.from('participants').select('id,team_id,full_name,email,contact_number,year_of_study,department,course_type,is_leader').in('team_id', teamIds),
        supabase.from('faculty_mentors').select('id,team_id,name,designation,department,contact_number,email').in('team_id', teamIds),
        supabase.from('payments').select('id,team_id,status,amount').in('team_id', teamIds),
      ]);

      const partsMap: Record<string, TeamDetail['participants']> = {};
      (parts || []).forEach((p) => { if (!partsMap[p.team_id]) partsMap[p.team_id] = []; partsMap[p.team_id].push(p); });
      const mentMap: Record<string, TeamDetail['faculty']> = {};
      (ments || []).forEach((m) => { mentMap[m.team_id] = { id: m.id, name: m.name, designation: m.designation, department: m.department, contact_number: m.contact_number, email: m.email }; });
      const payMap: Record<string, TeamDetail['payment']> = {};
      (pays || []).forEach((p) => { payMap[p.team_id] = { id: p.id, status: p.status, amount: Number(p.amount) }; });

      setTeams(teamsData.map((t) => ({
        ...t,
        participants: partsMap[t.id] || [],
        faculty: mentMap[t.id] || null,
        payment: payMap[t.id] || null,
      })));
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTeams(); }, [fetchTeams]);

  let filtered = teams;
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter((t) => t.team_name.toLowerCase().includes(q) || t.college_name.toLowerCase().includes(q) || t.registration_id.toLowerCase().includes(q) || t.team_email.toLowerCase().includes(q));
  }
  if (filterStatus !== 'All') filtered = filtered.filter((t) => t.status === filterStatus.toLowerCase());
  if (filterPayment !== 'All') filtered = filtered.filter((t) => (t.payment?.status || 'pending') === filterPayment.toLowerCase());

  return (
    <AuthGuard requiredRole="organizer">
      <OrganizerLayout title="Team Management" breadcrumbs={[{ label: 'Dashboard', href: '/dashboard/organizer' }, { label: 'Teams' }]}>
        {fetchError && (
          <div className="mb-5 p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
            Failed to load: {fetchError}. <button onClick={fetchTeams} className="underline font-medium cursor-pointer">Retry</button>
          </div>
        )}

        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 mb-5">
          <div className="relative w-full lg:w-80">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search teams..." className="w-full pl-9 pr-4 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-secondary-400 outline-none transition-colors" />
          </div>
          <div className="flex gap-2 flex-wrap">
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {['All', 'Verified', 'Registered'].map((s) => (
                <button key={s} onClick={() => setFilterStatus(s)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${filterStatus === s ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>{s}</button>
              ))}
            </div>
            <select value={filterPayment} onChange={(e) => setFilterPayment(e.target.value)} className="px-3 py-1.5 rounded-full text-xs font-medium border border-background-200 bg-background-50 text-foreground-600 cursor-pointer outline-none">
              <option value="All">All Payments</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>

        <div className="bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-secondary-400 border-t-transparent rounded-full animate-spin"></div></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200/70 bg-background-100/50">
                    {['Team', 'College', 'Dept', 'City', 'Members', 'Mentor', 'Payment', 'Status', 'Actions'].map((l) => (
                      <th key={l} className="px-4 py-3 text-left text-xs font-semibold text-foreground-500 whitespace-nowrap">{l}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((team) => (
                    <tr key={team.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-foreground-900 text-sm">{team.team_name}</p>
                        <code className="text-[10px] bg-background-100 px-1.5 py-0.5 rounded text-foreground-500">{team.registration_id}</code>
                      </td>
                      <td className="px-4 py-3 text-xs text-foreground-600 whitespace-nowrap">{team.college_name}</td>
                      <td className="px-4 py-3 text-xs text-foreground-600 whitespace-nowrap">{team.department}</td>
                      <td className="px-4 py-3 text-xs text-foreground-600 whitespace-nowrap">{team.city}</td>
                      <td className="px-4 py-3 text-xs text-foreground-600 whitespace-nowrap">{team.participants.length}</td>
                      <td className="px-4 py-3 text-xs text-foreground-600 whitespace-nowrap">{team.faculty ? team.faculty.name : '—'}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold ${team.payment?.status === 'paid' ? 'bg-accent-100 text-accent-700' : team.payment?.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                          {team.payment?.status === 'paid' ? 'Paid' : team.payment?.status === 'failed' ? 'Failed' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold ${team.status === 'verified' ? 'bg-accent-100 text-accent-700' : 'bg-background-200 text-foreground-600'}`}>
                          {team.status === 'verified' ? 'Verified' : 'Registered'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <button onClick={() => { setSelectedTeam(team); setShowDetailModal(true); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-secondary-50 text-foreground-400 hover:text-secondary-600 transition-colors cursor-pointer" title="View"><i className="ri-eye-line text-sm"></i></button>
                          <button className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-accent-50 text-foreground-400 hover:text-accent-600 transition-colors cursor-pointer" title="Download PDF"><i className="ri-file-pdf-line text-sm"></i></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan={9} className="px-4 py-12 text-center text-foreground-400">No teams found</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {showDetailModal && selectedTeam && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowDetailModal(false)}>
            <div className="bg-background-50 rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-background-50 border-b border-background-200/70 p-5 flex items-center justify-between z-10">
                <div><h3 className="font-heading text-lg font-bold text-foreground-950">{selectedTeam.team_name}</h3><p className="text-xs text-foreground-500">{selectedTeam.registration_id}</p></div>
                <button onClick={() => setShowDetailModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"><i className="ri-close-line text-lg"></i></button>
              </div>
              <div className="p-5 space-y-6">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {[['College', selectedTeam.college_name], ['Department', selectedTeam.department], ['City', selectedTeam.city], ['Email', selectedTeam.team_email], ['Phone', selectedTeam.team_phone], ['Status', selectedTeam.status], ['Payment', selectedTeam.payment?.status || 'N/A']].map(([l, v]) => (
                    <div key={l}><span className="text-foreground-400 text-xs">{l}:</span><p className="font-medium text-foreground-800">{v}</p></div>
                  ))}
                </div>
                <div>
                  <h4 className="font-heading font-semibold text-foreground-800 text-sm mb-3">Team Members</h4>
                  <div className="space-y-2">
                    {selectedTeam.participants.map((p) => (
                      <div key={p.id} className="flex items-center justify-between p-3 rounded-lg bg-background-100 border border-background-200/50">
                        <div><p className="text-sm font-medium text-foreground-800">{p.full_name}{p.is_leader && <span className="text-secondary-600 text-xs ml-2">(Team Leader)</span>}</p><p className="text-xs text-foreground-500">{p.email} &bull; {p.course_type || ''} &bull; {p.year_of_study} &bull; {p.contact_number} &bull; {p.department}</p></div>
                      </div>
                    ))}
                  </div>
                </div>
                {selectedTeam.faculty && (
                  <div><h4 className="font-heading font-semibold text-foreground-800 text-sm mb-2">Faculty Mentor</h4><div className="p-3 rounded-lg bg-background-100 border border-background-200/50"><p className="text-sm font-medium text-foreground-800">{selectedTeam.faculty.name}</p><p className="text-xs text-foreground-500">{selectedTeam.faculty.designation} &bull; {selectedTeam.faculty.department}</p><p className="text-xs text-foreground-500">{selectedTeam.faculty.email} &bull; {selectedTeam.faculty.contact_number}</p></div></div>
                )}
              </div>
            </div>
          </div>
        )}
      </OrganizerLayout>
    </AuthGuard>
  );
}